import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import OddsInput from './OddsInput';
import './UpcomingFights.css';

// ── Helpers ───────────────────────────────────────────────────

const formatOdds = (val) => {
  if (val === null || val === undefined) return null;
  const r = Math.round(val);
  return r >= 0 ? `+${r}` : `${r}`;
};

const impliedProb = (odds) => {
  if (!odds) return null;
  return odds > 0
    ? (100 / (odds + 100) * 100).toFixed(1)
    : (Math.abs(odds) / (Math.abs(odds) + 100) * 100).toFixed(1);
};

const decimalToAmerican = (dec) => {
  if (!dec) return null;
  return dec >= 2.0 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));
};

const getOddsAge = (ts) => {
  if (!ts) return null;
  const h = (Date.now() - new Date(ts)) / 3600000;
  if (h < 1) return '<1h';
  if (h < 24) return `${Math.floor(h)}h`;
  return `${Math.floor(h / 24)}d`;
};

const formatDate = (s) => {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
};

const formatMethod = (method, round) => {
  if (!method) return '—';
  return round ? `${method} · R${round}` : method;
};

const WC_ABBREV = {
  'Strawweight': 'STW', 'Flyweight': 'FLW', 'Bantamweight': 'BW',
  'Featherweight': 'FW', 'Lightweight': 'LW', 'Welterweight': 'WW',
  'Middleweight': 'MW', 'Light Heavyweight': 'LHW', 'Heavyweight': 'HW',
  "Women's Strawweight": 'WSW', "Women's Flyweight": 'WFL',
  "Women's Bantamweight": 'WBW', "Women's Featherweight": 'WFW',
  'Catchweight': 'CW',
};
const abbreviateWC = (wc) => WC_ABBREV[wc] || wc;

const getEdgeSignal = (fight) => {
  if (!fight.best_bet) return { cls: '', label: null };
  const edge = fight.best_bet === 'A' ? fight.edge_a : fight.edge_b;
  if (edge >= 12) return { cls: 'row-edge-gold',  label: 'HIGH CONVICTION' };
  if (edge >= 7)  return { cls: 'row-edge-green', label: 'STRONG EDGE' };
  if (edge >= 3)  return { cls: 'row-edge-blue',  label: 'SOLID EDGE' };
  return { cls: '', label: null };
};


const getReliability = (fight) => {
  const pr = fight.prediction_reliability;
  if (!pr || pr.score === undefined) return null;
  const s = pr.score;
  if (s >= 0.7) return { cls: 'rel-high',   label: 'High', color: '#34d399' };
  if (s >= 0.4) return { cls: 'rel-medium', label: 'Med',  color: '#fbbf24' };
  return                { cls: 'rel-low',    label: 'Low',  color: '#f87171' };
};

const ResultChip = ({ result }) => {
  if (!result) return <span className="td-dash">—</span>;
  const map = { win: ['W', 'result-w'], loss: ['L', 'result-l'], draw: ['D', 'result-d'], nc: ['NC', 'result-d'] };
  const [label, cls] = map[result] || [result.toUpperCase(), 'result-d'];
  return <span className={`result-chip ${cls}`}>{label}</span>;
};

// ── Fighter name + hover tooltip ──────────────────────────────

const FighterName = ({ fighterId, name }) => {
  const [tip, setTip] = useState(null);
  const [show, setShow] = useState(false);

  const onEnter = async () => {
    setShow(true);
    if (!tip) {
      try {
        const d = await fetch(`/api/fighter/${fighterId}/summary`).then(r => r.json());
        setTip(d);
      } catch (_) {}
    }
  };

  return (
    <div className="fn-wrap" onMouseEnter={onEnter} onMouseLeave={() => setShow(false)}>
      <span className="fn-name">{name}</span>
      {show && tip && (
        <div className="fn-tip">
          <div className="fnt-head">{tip.name}</div>
          <div className="fnt-record">
            {tip.wins}–{tip.losses}{tip.draws > 0 ? `–${tip.draws}` : ''}
          </div>
          {tip.style && (
            <div className="fnt-row"><span>Style</span><b>{tip.style.replace(/_/g, ' ')}</b></div>
          )}
          {tip.elo && <div className="fnt-row"><span>Elo</span><b>{tip.elo}</b></div>}
          {tip.power_rating && <div className="fnt-row"><span>Power</span><b>{tip.power_rating}</b></div>}
          {tip.reach_inches && <div className="fnt-row"><span>Reach</span><b>{tip.reach_inches}"</b></div>}
          {tip.last_fights?.length > 0 && (
            <div className="fnt-fights">
              <div className="fnt-fights-label">Last 3</div>
              {tip.last_fights.map((f, i) => (
                <div key={i} className={`fnt-fight fnt-${f.result}`}>
                  {f.result[0].toUpperCase()} vs {f.opponent}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Analysis panel (expanded row content) ────────────────────

const Analysis = ({ fight }) => {
  const [showExtra, setShowExtra] = useState(false);
  const impA = impliedProb(fight.odds_a);
  const impB = impliedProb(fight.odds_b);
  const edgeSignal = getEdgeSignal(fight);

  const hasBet = fight.best_bet === 'A' || fight.best_bet === 'B';
  const isA = fight.best_bet === 'A';
  const betFighter = isA ? fight.fighter_a : fight.fighter_b;
  const betEdge = isA ? fight.edge_a : fight.edge_b;
  const betOdds = isA ? fight.odds_a : fight.odds_b;
  const betBk = isA ? fight.odds_a_bookmaker : fight.odds_b_bookmaker;
  const betKelly = isA
    ? (fight.portfolio_kelly_bet_a || fight.kelly_bet_a)
    : (fight.portfolio_kelly_bet_b || fight.kelly_bet_b);

  const parseReasoning = (text) =>
    text.split('\n').map((line, i) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <div key={i} className={line.startsWith('• ') ? 'r-bullet' : 'r-line'}>
          {parts.map((p, j) =>
            p.startsWith('**') && p.endsWith('**')
              ? <strong key={j}>{p.slice(2, -2)}</strong>
              : <span key={j}>{p}</span>
          )}
        </div>
      );
    });

  const sides = [
    { name: fight.fighter_a, prob: fight.prob_a, rating: fight.rating_a, odds: fight.odds_a, bk: fight.odds_a_bookmaker, imp: impA, edge: fight.edge_a, kelly: fight.kelly_bet_a, portfolioKelly: fight.portfolio_kelly_bet_a, nBets: fight.n_bets_on_card_a },
    { name: fight.fighter_b, prob: fight.prob_b, rating: fight.rating_b, odds: fight.odds_b, bk: fight.odds_b_bookmaker, imp: impB, edge: fight.edge_b, kelly: fight.kelly_bet_b, portfolioKelly: fight.portfolio_kelly_bet_b, nBets: fight.n_bets_on_card_b },
  ];

  const hasExtra = (fight.glicko2_a || fight.glicko2_b) ||
                   (fight.line_movement_a || fight.line_movement_b);

  return (
    <div className="analysis-panel">

      {/* The Bet — hero */}
      {hasBet && (
        <div className="ap-bet-hero">
          <div className="ap-bet-hero-pick">Model Pick</div>
          <div className="ap-bet-hero-name">
            {betFighter}
            {edgeSignal.label && (
              <span className={`ap-hero-signal sig-${edgeSignal.cls.replace('row-edge-', '')}`}>
                {' · '}{edgeSignal.label}
              </span>
            )}
          </div>
          {(betEdge !== null || betOdds || betKelly) && (
            <div className="ap-bet-hero-detail">
              {betEdge !== null && `${betEdge > 0 ? '+' : ''}${betEdge}% edge`}
              {betOdds && ` · ${formatOdds(betOdds)}${betBk ? ` (${betBk})` : ''}`}
              {betKelly > 0 && ` · $${betKelly} Kelly`}
            </div>
          )}
        </div>
      )}

      {/* Reliability warning */}
      {fight.prediction_reliability && fight.prediction_reliability.score < 0.4 && fight.prediction_reliability.reasons.length > 0 && (
        <div className="ap-reliability-warn">
          <span className="ap-rel-icon">!</span>
          <div className="ap-rel-content">
            <div className="ap-rel-title">Low reliability ({(fight.prediction_reliability.score * 100).toFixed(0)}%)</div>
            {fight.prediction_reliability.reasons.map((r, i) => (
              <div key={i} className="ap-rel-reason">{r}</div>
            ))}
          </div>
        </div>
      )}

      {/* Why — reasoning */}
      {fight.bet_reasoning && (
        <div className="ap-reasoning">{parseReasoning(fight.bet_reasoning)}</div>
      )}
      {!fight.bet_reasoning && (
        <p className="ap-no-reason">Add odds to both fighters to see edge calculations and betting recommendations.</p>
      )}

      {/* The Numbers — compare grid */}
      <div className="ap-compare">
        {sides.map((s, i) => (
          <div key={i} className="apc-col">
            <div className="apc-name">{s.name}</div>
            <div className="apc-row"><span>Model prediction</span><b>{s.prob}%</b></div>
            {s.rating && <div className="apc-row"><span>Power rating</span><b>{s.rating}</b></div>}
            {s.odds && <div className="apc-row"><span>Market odds</span><b>{formatOdds(s.odds)}{s.bk ? ` · ${s.bk}` : ''}</b></div>}
            {s.imp && <div className="apc-row"><span>Market implied</span><b>{s.imp}%</b></div>}
            {s.edge !== null && (
              <div className="apc-row">
                <span>Edge</span>
                <b className={s.edge > 0 ? 'apc-pos' : 'apc-neg'}>{s.edge > 0 ? '+' : ''}{s.edge}%</b>
              </div>
            )}
            {s.kelly > 0 && <div className="apc-row"><span>Kelly bet</span><b className="apc-kelly">${s.kelly}</b></div>}
            {s.portfolioKelly > 0 && s.portfolioKelly !== s.kelly && (
              <div className="apc-row"><span>Portfolio adj. ({s.nBets} bets)</span><b className="apc-kelly">${s.portfolioKelly}</b></div>
            )}
          </div>
        ))}
      </div>

      {/* Method of Victory — always visible when analysis is open */}
      {(fight.method_a || fight.method_b) && (
        <div className="ap-method">
          <div className="ap-method-title">
            Method of Victory (if winner)
            {fight.totals_bet && (
              <span className="ap-method-bet">
                <span className="amb-label">
                  {fight.totals_bet.side === 'finish' ? 'Finish' : 'Decision'} bet suggestion:
                </span>
                <span className="amb-line">{fight.totals_bet.label}</span>
                <span className="amb-odds">{fight.totals_bet.odds_decimal.toFixed(2)}</span>
                <span className="amb-bk">{fight.totals_bet.bookmaker}</span>
                <span className="amb-edge">+{fight.totals_bet.edge}% edge</span>
                <span className="amb-kelly">${fight.totals_bet.kelly_bet} Kelly</span>
              </span>
            )}
          </div>
          <div className="ap-method-grid">
            {[
              { name: fight.fighter_a, m: fight.method_a },
              { name: fight.fighter_b, m: fight.method_b },
            ].map((s, i) => (
              <div key={i} className="apm-col">
                <div className="apc-name">{s.name}</div>
                {s.m ? (
                  <>
                    <div className="apm-bar-row">
                      <span className="apm-label">KO/TKO</span>
                      <div className="apm-bar"><div className="apm-fill apm-ko" style={{ width: `${(s.m.ko_prob * 100).toFixed(0)}%` }} /></div>
                      <b className="apm-pct">{(s.m.ko_prob * 100).toFixed(0)}%</b>
                    </div>
                    <div className="apm-bar-row">
                      <span className="apm-label">Submission</span>
                      <div className="apm-bar"><div className="apm-fill apm-sub" style={{ width: `${(s.m.sub_prob * 100).toFixed(0)}%` }} /></div>
                      <b className="apm-pct">{(s.m.sub_prob * 100).toFixed(0)}%</b>
                    </div>
                    <div className="apm-bar-row">
                      <span className="apm-label">Decision</span>
                      <div className="apm-bar"><div className="apm-fill apm-dec" style={{ width: `${(s.m.dec_prob * 100).toFixed(0)}%` }} /></div>
                      <b className="apm-pct">{(s.m.dec_prob * 100).toFixed(0)}%</b>
                    </div>
                  </>
                ) : (
                  <div className="apm-no-data">No data</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extra Context toggle */}
      {hasExtra && (
        <button className="ap-extra-toggle" onClick={() => setShowExtra(v => !v)}>
          {showExtra ? 'Less ▴' : 'More ▾'}
        </button>
      )}

      {/* Extra Context — collapsible */}
      {showExtra && (
        <>
          {/* Glicko-2 ratings if available */}
          {(fight.glicko2_a || fight.glicko2_b) && (
            <div className="ap-glicko2">
              <div className="ap-method-title">Glicko-2 Ratings</div>
              <div className="ap-method-grid">
                {[
                  { name: fight.fighter_a, g: fight.glicko2_a, rd: fight.rd_a },
                  { name: fight.fighter_b, g: fight.glicko2_b, rd: fight.rd_b },
                ].map((s, i) => (
                  <div key={i} className="apm-col">
                    <div className="apc-name">{s.name}</div>
                    {s.g ? (
                      <>
                        <div className="apc-row"><span>Rating</span><b>{s.g}</b></div>
                        <div className="apc-row"><span>RD (uncertainty)</span><b>{s.rd}</b></div>
                      </>
                    ) : (
                      <div className="apm-no-data">Not calculated</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Line Movement */}
          {(fight.line_movement_a || fight.line_movement_b) && (
            <div className="ap-line-movement">
              <div className="ap-method-title">Line Movement</div>
              <div className="ap-method-grid">
                {[
                  { name: fight.fighter_a, lm: fight.line_movement_a },
                  { name: fight.fighter_b, lm: fight.line_movement_b },
                ].map((s, i) => (
                  <div key={i} className="apm-col">
                    <div className="apc-name">{s.name}</div>
                    {s.lm ? (
                      s.lm.n_snapshots < 2 ? (
                        <div className="lm-tracking">Tracking… (1 snapshot)</div>
                      ) : (
                        <>
                          <div className={`lm-direction lm-${s.lm.direction}`}>
                            {s.lm.direction === 'shortened' && '↘ Shortened'}
                            {s.lm.direction === 'drifted'   && '↗ Drifted'}
                            {s.lm.direction === 'stable'    && '→ Stable'}
                            <span className="lm-pct">
                              {s.lm.change_pct > 0 ? '+' : ''}{s.lm.change_pct}%
                            </span>
                          </div>
                          <div className="lm-odds-flow">
                            <span className="lm-open">{formatOdds(s.lm.opening_am)}</span>
                            <span className="lm-arr">→</span>
                            <span className="lm-curr">{formatOdds(s.lm.current_am)}</span>
                          </div>
                          <div className="lm-info">
                            {s.lm.n_snapshots} snapshots · {s.lm.n_days} day{s.lm.n_days !== 1 ? 's' : ''}
                          </div>
                        </>
                      )
                    ) : (
                      <div className="apm-no-data">No odds data</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────

function UpcomingFights() {
  const [fights, setFights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showValueOnly, setShowValueOnly] = useState(false);
  const [weightClassFilter, setWeightClassFilter] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [oddsInputFight, setOddsInputFight] = useState(null);
  const [viewMode, setViewMode] = useState('upcoming');
  const [showPropCols, setShowPropCols] = useState(
    () => localStorage.getItem('ufc_show_prop_cols') === 'true'
  );
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia('(max-width: 600px)').matches
  );
  const [searchParams] = useSearchParams();

  const togglePropCols = () => setShowPropCols(prev => {
    const next = !prev;
    localStorage.setItem('ufc_show_prop_cols', next);
    return next;
  });

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 600px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const totalCols = isMobile ? 6 : (showPropCols ? 13 : 8);

  const eventId = searchParams.get('event_id');
  const isSingleEvent = Boolean(eventId);

  useEffect(() => { fetchFights(); }, [searchParams, viewMode]);

  const fetchFights = async () => {
    setLoading(true);
    try {
      let url;
      if (eventId) {
        url = `/api/upcoming-fights?event_id=${eventId}`;
      } else {
        const s = viewMode === 'past' ? 'completed' : 'upcoming';
        url = `/api/upcoming-fights?status=${s}&event_limit=4`;
      }
      const data = await fetch(url).then(r => r.json());
      setFights(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isPast = fights.length > 0 && 'result_a' in fights[0];

  let displayed = fights;
  if (!isPast && showValueOnly) displayed = displayed.filter(f => f.best_bet !== null);
  if (weightClassFilter) displayed = displayed.filter(f => f.weight_class === weightClassFilter);

  // Group by event (preserving order)
  const eventOrder = [];
  const eventMap = {};
  for (const f of displayed) {
    if (!eventMap[f.event_id]) {
      eventMap[f.event_id] = { event_id: f.event_id, event_name: f.event_name, event_date: f.event_date, location: f.location, fights: [] };
      eventOrder.push(f.event_id);
    }
    eventMap[f.event_id].fights.push(f);
  }
  const groups = eventOrder.map(id => eventMap[id]);

  const weightClasses = Array.from(new Set(fights.map(f => f.weight_class).filter(Boolean))).sort();
  const valueBetCount = isPast ? 0 : fights.filter(f => f.best_bet !== null).length;
  const singleEventMeta = isSingleEvent && groups[0] ? groups[0] : null;

  const modelWarnings = !isPast ? displayed.reduce((acc, f) => {
    if (f.prob_a === null || f.prob_method !== 'gbm_model') {
      acc.push({ fight: `${f.fighter_a} vs ${f.fighter_b}`, error: f.model_error });
    }
    return acc;
  }, []) : [];

  if (loading) return <div className="loading">Loading fights...</div>;

  return (
    <div className="upcoming-fights">

      {/* Filter bar */}
      <div className="uf-bar">
        <div className="uf-bar-left">
          {isSingleEvent && <Link to="/events" className="uf-back">← Events</Link>}
          <span className="uf-count">{displayed.length} fights</span>
          {valueBetCount > 0 && (
            <span className="uf-count-val">{valueBetCount} value bet{valueBetCount !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="uf-bar-right">
          {!isSingleEvent && (
            <div className="uf-toggle">
              <button className={`uf-tog ${viewMode === 'upcoming' ? 'uf-tog-on' : ''}`} onClick={() => setViewMode('upcoming')}>{isMobile ? 'Up' : 'Upcoming'}</button>
              <button className={`uf-tog ${viewMode === 'past' ? 'uf-tog-on' : ''}`} onClick={() => setViewMode('past')}>Past</button>
            </div>
          )}
          {!isPast && (
            <div className="uf-toggle">
              <button className={`uf-tog ${!showValueOnly ? 'uf-tog-on' : ''}`} onClick={() => setShowValueOnly(false)}>All</button>
              <button className={`uf-tog ${showValueOnly ? 'uf-tog-on' : ''}`} onClick={() => setShowValueOnly(true)}>{isMobile ? 'Value' : 'Value Bets'}</button>
            </div>
          )}
          {weightClasses.length > 1 && (
            <select className="uf-wc" value={weightClassFilter} onChange={e => setWeightClassFilter(e.target.value)}>
              <option value="">All classes</option>
              {weightClasses.map(wc => <option key={wc} value={wc}>{wc}</option>)}
            </select>
          )}
          {!isPast && !isMobile && (
            <div className="uf-toggle">
              <button className={`uf-tog ${showPropCols ? 'uf-tog-on' : ''}`} onClick={togglePropCols}>
                Props
              </button>
            </div>
          )}
          <button className="uf-refresh" onClick={fetchFights} title="Refresh">↺</button>
        </div>
      </div>

      {/* Model warning banner */}
      {modelWarnings.length > 0 && (
        <div className="model-warning-banner">
          <span className="mwb-icon">⚠</span>
          <span className="mwb-text">
            <strong>No prediction</strong> — GBM unavailable for {modelWarnings.length} fight{modelWarnings.length !== 1 ? 's' : ''}:{' '}
            {modelWarnings.map((w, i) => (
              <span key={i} className="mwb-fight" title={w.error || undefined}>
                {w.fight}{w.error ? ' — hover for error' : ''}
                {i < modelWarnings.length - 1 ? ', ' : ''}
              </span>
            ))}
          </span>
        </div>
      )}

      {/* Single-event hero */}
      {isSingleEvent && singleEventMeta && (
        <div className="uf-event-hero">
          <div className="ueh-name">{singleEventMeta.event_name}</div>
          <div className="ueh-meta">
            {formatDate(singleEventMeta.event_date)}
            {singleEventMeta.location ? ` · ${singleEventMeta.location}` : ''}
          </div>
        </div>
      )}

      {displayed.length === 0 ? (
        <div className="uf-empty">
          {showValueOnly
            ? 'No value bets found. Try "All" or run the odds scraper.'
            : 'No fights found. Run the Events scraper to load upcoming cards.'}
        </div>
      ) : (
        <div className="fights-wrap">
          <table className="fights-table">
            {/* base 8 cols: ctx + fighter + model + market + edge + odds + kelly + actions */}
            {/* prop 5 cols: prop model + prop market + prop edge + prop odds + victory bet */}
            <colgroup>
              <col style={{ width: isMobile ? '5%' : '130px' }} />
              <col style={{ width: isMobile ? '25%' : undefined }} />
              <col style={{ width: isMobile ? '13%' : '68px' }} />
              <col style={{ width: isMobile ? '13%' : '75px' }} />
              {!isMobile && <col style={{ width: '78px' }} />}
              <col style={{ width: isMobile ? '18%' : '110px' }} />
              <col style={{ width: isMobile ? '26%' : '105px' }} />
              {!isMobile && showPropCols && <col style={{ width: '68px' }} />}
              {!isMobile && showPropCols && <col style={{ width: '75px' }} />}
              {!isMobile && showPropCols && <col style={{ width: '78px' }} />}
              {!isMobile && showPropCols && <col style={{ width: '90px' }} />}
              {!isMobile && showPropCols && <col style={{ width: '90px' }} />}
              {!isMobile && <col style={{ width: '76px' }} />}
            </colgroup>
            <thead>
              <tr>
                <th className="th-left"></th>
                <th className="th-left">Fighter</th>
                <th className="th-right">{isMobile ? '%' : 'Model'}</th>
                <th className="th-right">{isMobile ? 'Mkt' : 'Market'}</th>
                {!isMobile && <th className="th-right">Edge</th>}
                <th className="th-right">{isMobile ? 'Odds' : 'Best Odds'}</th>
                <th className="th-right">{isMobile ? 'Bet' : 'Kelly Bet'}</th>
                {!isMobile && showPropCols && <th className="th-right th-prop-sep">P.Model</th>}
                {!isMobile && showPropCols && <th className="th-right">P.Market</th>}
                {!isMobile && showPropCols && <th className="th-right">P.Edge</th>}
                {!isMobile && showPropCols && <th className="th-right">Prop Odds</th>}
                {!isMobile && showPropCols && <th className="th-right">Victory Bet</th>}
                {!isMobile && <th></th>}
              </tr>
            </thead>
            <tbody>
              {groups.map(group => (
                <React.Fragment key={group.event_id}>

                  {/* Event group header (multi-event view) */}
                  {!isSingleEvent && (
                    <tr className="tr-group">
                      <td colSpan={totalCols}>
                        <span className="eg-name">{group.event_name}</span>
                        <span className="eg-meta">
                          {formatDate(group.event_date)}
                          {group.location ? ` · ${group.location}` : ''}
                        </span>
                        <span className="eg-count">{group.fights.length} fights</span>
                      </td>
                    </tr>
                  )}

                  {/* Group fights by card section (Main Card first, then Prelims) */}
                  {(() => {
                    const SECTION_ORDER = ['main', 'prelims', 'early_prelims'];
                    const SECTION_LABELS = { main: 'Main Card', prelims: 'Prelims', early_prelims: 'Early Prelims' };
                    const sectionMap = {};
                    for (const fight of group.fights) {
                      const sec = fight.card_section || 'main';
                      if (!sectionMap[sec]) sectionMap[sec] = [];
                      sectionMap[sec].push(fight);
                    }
                    const sectionKeys = SECTION_ORDER.filter(s => sectionMap[s]);
                    const showSections = sectionKeys.length > 1;

                    return sectionKeys.map(section => (
                      <React.Fragment key={`section-${group.event_id}-${section}`}>
                        {showSections && (
                          <tr className="tr-section">
                            <td colSpan={totalCols}>{SECTION_LABELS[section]}</td>
                          </tr>
                        )}
                        {sectionMap[section].map(fight => {
                    const isExp = expanded.has(fight.fight_id);
                    const isBetA = fight.best_bet === 'A';
                    const isBetB = fight.best_bet === 'B';
                    const edgeSignal = getEdgeSignal(fight);
                    const reliability = getReliability(fight);
                    const isLowReliability = reliability && reliability.cls === 'rel-low';

                    // Which fighter row the victory prop bet aligns with
                    const vbetIsA = (() => {
                      if (!fight.totals_bet || !fight.method_a || !fight.method_b) return true;
                      const pA = (fight.prob_a || 50) / 100;
                      const pB = (fight.prob_b || 50) / 100;
                      const finA = pA * (fight.method_a.ko_prob + fight.method_a.sub_prob);
                      const finB = pB * (fight.method_b.ko_prob + fight.method_b.sub_prob);
                      return fight.totals_bet.side === 'finish' ? finA >= finB : finA <= finB;
                    })();
                    const impA = impliedProb(fight.odds_a);
                    const impB = impliedProb(fight.odds_b);
                    const oddsAge = getOddsAge(fight.odds_last_updated);

                    return (
                      <React.Fragment key={fight.fight_id}>

                        {/* ── Fighter A row ── */}
                        <tr className={`tr-a ${edgeSignal.cls} ${isBetA ? 'tr-bet' : ''} ${isLowReliability ? 'tr-low-rel' : ''}`}
                          onClick={isMobile ? () => toggleExpanded(fight.fight_id) : undefined}
                          style={isMobile ? { cursor: 'pointer' } : undefined}>
                          <td rowSpan={2} className="td-ctx">
                            {fight.weight_class && !isMobile && <div className="ctx-wc">{fight.weight_class}</div>}
                            <div className="ctx-badges">
                              {!isMobile && fight.is_main_event && <span className="ctx-tag ctx-main">Main</span>}
                              {!isMobile && fight.is_title && <span className="ctx-tag ctx-title">Title</span>}
                              {!isPast && (fight.prob_a === null || fight.prob_method !== 'gbm_model') && (
                                <span className="ctx-tag ctx-model-warn" title={fight.model_error || 'GBM unavailable for this fight'}>
                                  {isMobile ? '!' : 'No pred'}
                                </span>
                              )}
                              {!isPast && (() => {
                                const rel = getReliability(fight);
                                if (!rel) return null;
                                const pr = fight.prediction_reliability;
                                const tip = pr.reasons.length > 0
                                  ? `Reliability: ${(pr.score * 100).toFixed(0)}%\n${pr.reasons.join('\n')}`
                                  : `Reliability: ${(pr.score * 100).toFixed(0)}%`;
                                return (
                                  <span className={`ctx-tag ctx-reliability ${rel.cls}`} title={tip}>
                                    {isMobile ? rel.label[0] : `${rel.label} ${(pr.score * 100).toFixed(0)}%`}
                                  </span>
                                );
                              })()}
                            </div>
                            {!isMobile && oddsAge && <div className="ctx-age">{oddsAge} ago</div>}
                            {fight.method && <div className="ctx-method">{formatMethod(fight.method, fight.round)}</div>}
                          </td>
                          <td className="td-fighter">
                            <div className="fighter-cell">
                              {isPast && <ResultChip result={fight.result_a} />}
                              <FighterName fighterId={fight.fighter_a_id} name={fight.fighter_a} />
                            </div>
                          </td>
                          <td className="td-num">{fight.prob_a !== null ? `${fight.prob_a}%` : '—'}</td>
                          <td className="td-num td-muted">{impA ? `${impA}%` : '—'}</td>
                          {!isMobile && (
                            <td className={`td-num td-edge ${fight.edge_a > 0 ? 'edge-pos' : 'edge-neg'}`}>
                              {fight.best_bet === 'A' && edgeSignal.label && (
                                <span className={`edge-signal sig-${edgeSignal.cls.replace('row-edge-', '')}`}>
                                  {edgeSignal.label}
                                </span>
                              )}
                              {fight.edge_a !== null
                                ? `${fight.edge_a > 0 ? '+' : ''}${fight.edge_a}%`
                                : '—'}
                            </td>
                          )}
                          <td className="td-odds">
                            {fight.odds_a !== null ? (
                              <>
                                <span className="odds-val">{formatOdds(fight.odds_a)}</span>
                                {fight.odds_a_bookmaker && (
                                  <span className="odds-bk">{fight.odds_a_bookmaker}</span>
                                )}
                              </>
                            ) : (
                              !isPast && (
                                <button className="add-odds-btn"
                                  onClick={() => setOddsInputFight({ ...fight, selectedFighter: 'A' })}>
                                  + odds
                                </button>
                              )
                            )}
                          </td>
                          <td className="td-kelly">
                            {isBetA && fight.kelly_bet_a ? (
                              <span className="kelly-chip">{isMobile ? '$' : 'BET $'}{fight.portfolio_kelly_bet_a || fight.kelly_bet_a}</span>
                            ) : <span className="td-dash">—</span>}
                          </td>
                          {!isMobile && showPropCols && (
                            <td className="td-num td-prop-sep">
                              {!isPast && fight.totals_bet && vbetIsA
                                ? `${fight.totals_bet.model_prob}%`
                                : <span className="td-dash">—</span>}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className="td-num td-muted">
                              {!isPast && fight.totals_bet && vbetIsA
                                ? `${fight.totals_bet.implied_prob}%`
                                : <span className="td-dash">—</span>}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className={`td-num td-edge ${!isPast && fight.totals_bet && vbetIsA ? 'edge-pos' : ''}`}>
                              {!isPast && fight.totals_bet && vbetIsA
                                ? `+${fight.totals_bet.edge}%`
                                : <span className="td-dash">—</span>}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className="td-odds">
                              {!isPast && fight.totals_bet && vbetIsA ? (
                                <>
                                  <span className="odds-val">{formatOdds(decimalToAmerican(fight.totals_bet.odds_decimal))}</span>
                                  <span className="odds-bk">{fight.totals_bet.bookmaker}</span>
                                </>
                              ) : (
                                <span className="td-dash">—</span>
                              )}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className="td-vbet">
                              {!isPast && fight.totals_bet && vbetIsA ? (
                                <>
                                  <span className="kelly-chip">BET ${fight.totals_bet.kelly_bet}</span>
                                  <span className="odds-bk">{fight.totals_bet.label}</span>
                                </>
                              ) : (
                                <span className="td-dash">—</span>
                              )}
                            </td>
                          )}
                          {!isMobile && (
                            <td rowSpan={2} className="td-act">
                              <button
                                className={`act-expand ${isExp ? 'act-open' : ''}`}
                                onClick={() => toggleExpanded(fight.fight_id)}
                                title="Toggle analysis"
                              >
                                {isExp ? '▲' : '▼'}
                              </button>
                            </td>
                          )}
                        </tr>

                        {/* ── Fighter B row ── */}
                        <tr className={`tr-b ${edgeSignal.cls} ${isBetB ? 'tr-bet' : ''} ${!isExp ? 'tr-last' : ''} ${isLowReliability ? 'tr-low-rel' : ''}`}
                          onClick={isMobile ? () => toggleExpanded(fight.fight_id) : undefined}
                          style={isMobile ? { cursor: 'pointer' } : undefined}>
                          <td className="td-fighter">
                            <div className="fighter-cell">
                              {isPast && <ResultChip result={fight.result_b} />}
                              <FighterName fighterId={fight.fighter_b_id} name={fight.fighter_b} />
                            </div>
                          </td>
                          <td className="td-num">{fight.prob_b !== null ? `${fight.prob_b}%` : '—'}</td>
                          <td className="td-num td-muted">{impB ? `${impB}%` : '—'}</td>
                          {!isMobile && (
                            <td className={`td-num td-edge ${fight.edge_b > 0 ? 'edge-pos' : 'edge-neg'}`}>
                              {fight.best_bet === 'B' && edgeSignal.label && (
                                <span className={`edge-signal sig-${edgeSignal.cls.replace('row-edge-', '')}`}>
                                  {edgeSignal.label}
                                </span>
                              )}
                              {fight.edge_b !== null
                                ? `${fight.edge_b > 0 ? '+' : ''}${fight.edge_b}%`
                                : '—'}
                            </td>
                          )}
                          <td className="td-odds">
                            {fight.odds_b !== null ? (
                              <>
                                <span className="odds-val">{formatOdds(fight.odds_b)}</span>
                                {fight.odds_b_bookmaker && (
                                  <span className="odds-bk">{fight.odds_b_bookmaker}</span>
                                )}
                              </>
                            ) : (
                              !isPast && (
                                <button className="add-odds-btn"
                                  onClick={() => setOddsInputFight({ ...fight, selectedFighter: 'B' })}>
                                  + odds
                                </button>
                              )
                            )}
                          </td>
                          <td className="td-kelly">
                            {isBetB && fight.kelly_bet_b ? (
                              <span className="kelly-chip">{isMobile ? '$' : 'BET $'}{fight.portfolio_kelly_bet_b || fight.kelly_bet_b}</span>
                            ) : <span className="td-dash">—</span>}
                          </td>
                          {!isMobile && showPropCols && (
                            <td className="td-num td-prop-sep">
                              {!isPast && fight.totals_bet && !vbetIsA
                                ? `${fight.totals_bet.model_prob}%`
                                : <span className="td-dash">—</span>}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className="td-num td-muted">
                              {!isPast && fight.totals_bet && !vbetIsA
                                ? `${fight.totals_bet.implied_prob}%`
                                : <span className="td-dash">—</span>}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className={`td-num td-edge ${!isPast && fight.totals_bet && !vbetIsA ? 'edge-pos' : ''}`}>
                              {!isPast && fight.totals_bet && !vbetIsA
                                ? `+${fight.totals_bet.edge}%`
                                : <span className="td-dash">—</span>}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className="td-odds">
                              {!isPast && fight.totals_bet && !vbetIsA ? (
                                <>
                                  <span className="odds-val">{formatOdds(decimalToAmerican(fight.totals_bet.odds_decimal))}</span>
                                  <span className="odds-bk">{fight.totals_bet.bookmaker}</span>
                                </>
                              ) : (
                                <span className="td-dash">—</span>
                              )}
                            </td>
                          )}
                          {!isMobile && showPropCols && (
                            <td className="td-vbet">
                              {!isPast && fight.totals_bet && !vbetIsA ? (
                                <>
                                  <span className="kelly-chip">BET ${fight.totals_bet.kelly_bet}</span>
                                  <span className="odds-bk">{fight.totals_bet.label}</span>
                                </>
                              ) : (
                                <span className="td-dash">—</span>
                              )}
                            </td>
                          )}
                        </tr>

                        {/* ── Analysis row ── */}
                        {isExp && (
                          <tr className="tr-analysis">
                            <td colSpan={totalCols}>
                              <Analysis fight={fight} />
                            </td>
                          </tr>
                        )}

                      </React.Fragment>
                    );
                        })}
                      </React.Fragment>
                    ));
                  })()}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {oddsInputFight && (
        <OddsInput
          fight={oddsInputFight}
          onClose={() => { setOddsInputFight(null); fetchFights(); }}
        />
      )}
    </div>
  );
}

export default UpcomingFights;
